import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isRidesModuleEnabled, isSportsModuleEnabled } from '../../config/modules';
import BrandLogo from '../brand/BrandLogo';
import SharedFooter from './SharedFooter';

type MenuItem = {
  label: string;
  to: string;
  end?: boolean;
};

type MenuGroup = {
  label: string;
  items: MenuItem[];
};

const isPathActive = (pathname: string, item: MenuItem) => {
  if (item.end) {
    return pathname === item.to;
  }

  return pathname === item.to || pathname.startsWith(`${item.to}/`);
};

const desktopPillClass = (isActive: boolean) =>
  `${isActive ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'} inline-flex items-center rounded-full px-4 py-2 text-sm font-medium transition`;

const DesktopMenuGroup: React.FC<{
  group: MenuGroup;
  pathname: string;
}> = ({ group, pathname }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isGroupActive = group.items.some((item) => isPathActive(pathname, item));
  const menuStateClass = isOpen ? 'visible opacity-100' : 'invisible opacity-0';

  const closeMenu = () => {
    setIsOpen(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setIsOpen(false);
        }
      }}
    >
      <button type="button" className={desktopPillClass(isGroupActive)} aria-haspopup="true" aria-expanded={isOpen}>
        {group.label}
        <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
      </button>
      <div className={`${menuStateClass} absolute left-0 top-full z-50 w-64 pt-2 transition`}>
        <div className="max-h-[calc(100vh-6rem)] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-900/10">
          {group.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={closeMenu}
              className={`${isPathActive(pathname, item) ? 'bg-slate-100 text-slate-950' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'} block rounded-xl px-3 py-2 text-sm font-medium transition`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const MobileMenuGroup: React.FC<{
  group: MenuGroup;
  pathname: string;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}> = ({ group, pathname, isOpen, onToggle, onNavigate }) => {
  const isGroupActive = group.items.some((item) => isPathActive(pathname, item));

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex min-h-[50px] w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={isOpen}
      >
        <span className={`text-sm font-semibold ${isGroupActive ? 'text-slate-950' : 'text-slate-700'}`}>{group.label}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="border-t border-slate-100 px-2 py-2">
          {group.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={`${isPathActive(pathname, item) ? 'bg-slate-950 text-white' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'} block rounded-lg px-3 py-2.5 text-sm font-medium transition`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileGroup, setOpenMobileGroup] = useState('');
  const showSosAccess = currentUser?.role === 'TEACHER' || currentUser?.role === 'STUDENT';

  const navGroups = useMemo<MenuGroup[]>(() => {
    const sportsGroup: MenuGroup = {
      label: 'Sports',
      items: [
        { to: '/sports', label: 'Sports Home', end: true },
        { to: '/sports/dashboard', label: 'Sports Dashboard' },
        { to: '/sports/register', label: 'Sports Registration' },
        { to: '/sports/profile', label: 'My Sports Profile' },
        { to: '/sports/create-team', label: 'Create Team' },
        { to: '/sports/teams', label: 'Find Teams' },
        { to: '/sports/tournaments', label: 'Tournaments' },
        { to: '/sports/challenge-match', label: 'Challenge Match' },
      ],
    };
    const ridesGroup: MenuGroup = {
      label: 'Rides',
      items: [
        { to: '/rides', label: 'Rides Home', end: true },
        { to: '/rides/dashboard', label: 'Rides Dashboard' },
        { to: '/rides/book', label: 'Book Ride' },
        { to: '/rides/register', label: 'Rides Registration' },
        { to: '/rides/driver/register', label: 'Register Driver' },
        { to: '/rides/stand/register', label: 'Register Stand' },
        { to: '/rides/stands', label: 'Nearby Stands' },
      ],
    };
    const passengerRidesGroup: MenuGroup = {
      label: 'Rides',
      items: [
        { to: '/rides', label: 'Rides Home', end: true },
        { to: '/rides/book', label: 'Book Ride' },
        { to: '/rides/stands', label: 'Nearby Stands' },
      ],
    };
    const groups: MenuGroup[] =
      currentUser?.role === 'SPORTS' && isSportsModuleEnabled
        ? [sportsGroup, passengerRidesGroup]
        : currentUser?.role === 'RIDES' && isRidesModuleEnabled
          ? [ridesGroup]
          : [
              {
                label: 'Account',
              items: [
                { to: '/dashboard/legacy', label: 'Current Plan', end: true },
                { to: '/account/privacy', label: 'Privacy & Account' },
              ],
            },
            {
              label: 'Books',
              items: [
                { to: '/books', label: 'Books Home', end: true },
                { to: '/my-book-listings', label: 'My Listings' },
                { to: '/my-book-requests', label: 'My Requests' },
              ],
            },
            ...(isRidesModuleEnabled ? [passengerRidesGroup] : []),
          ];

    if (currentUser?.role === 'TEACHER') {
      groups.push({
        label: 'Teaching',
        items: [
          { to: '/teacher/dashboard', label: 'Teacher Dashboard' },
          { to: '/teacher/leads', label: 'Get Leads' },
          { to: '/teacher/payments', label: 'Manage Payments' },
          { to: '/education/institute', label: 'Institute Profile' },
          { to: '/education/demos', label: 'Demo Requests' },
          { to: '/education/homework', label: 'Homework' },
          { to: '/education/complaints', label: 'Complaints' },
          { to: '/education/subscription-usage', label: 'Subscription Usage' },
        ],
      });
    }

    if (currentUser?.role === 'STUDENT') {
      groups.push({
        label: 'Learning',
        items: [
          { to: '/student/dashboard', label: 'Student Dashboard' },
          { to: '/parent/dashboard', label: 'Parent Dashboard' },
          { to: '/student/requirements', label: 'Requirements' },
          { to: '/education/demos', label: 'Demo Classes' },
          { to: '/education/homework', label: 'Homework' },
          { to: '/education/complaints', label: 'Complaints' },
          { to: '/scholarship/exam', label: 'Scholarship' },
          { to: '/subscriptions', label: 'Manage Payments' },
          { to: '/education/subscription-usage', label: 'Subscription Usage' },
        ],
      });
    }

    if (currentUser?.role === 'SELLER') {
      groups.push({
        label: 'Seller',
        items: [
          { to: '/shop/dashboard', label: 'Seller Dashboard' },
          { to: '/book-shop/register', label: 'Register Shop' },
          { to: '/shop/products/add', label: 'Add Products' },
          { to: '/shop/book-requests', label: 'Book Requests' },
          { to: '/shop/buyback-offers', label: 'Buy-back Offers' },
          { to: '/shop/subscription', label: 'Shop Plans' },
        ],
      });
    }

    if (currentUser?.role === 'ADMIN') {
      groups.push({
        label: 'Admin',
        items: [
          { to: '/admin/operations', label: 'Admin Operations' },
          { to: '/admin/reschedule-approvals', label: 'Reschedule Approvals' },
          { to: '/admin/scholarship', label: 'Scholarship' },
          { to: '/admin/books', label: 'Books Admin' },
          { to: '/admin/education', label: 'Education Ops' },
          { to: '/admin/education/verification', label: 'Tutor Verification' },
          { to: '/admin/education/refunds', label: 'Refunds' },
          { to: '/admin/education/staff', label: 'Staff RBAC' },
          { to: '/admin/education/analytics', label: 'Analytics' },
          { to: '/admin/education/reminders', label: 'Reminders' },
          { to: '/admin/education/payments', label: 'Payment Checks' },
          { to: '/admin/education/subscriptions', label: 'Subscription Usage' },
          ...(isSportsModuleEnabled ? [{ to: '/admin/sports', label: 'Sports Admin' }] : []),
          ...(isRidesModuleEnabled ? [{ to: '/admin/rides', label: 'Rides Admin' }] : []),
        ],
      });
    }

    return groups;
  }, [currentUser?.role]);

  const activeMobileGroup =
    navGroups.find((group) => group.items.some((item) => isPathActive(location.pathname, item)))?.label ||
    navGroups[0]?.label ||
    '';

  useEffect(() => {
    if (isMobileMenuOpen) {
      setOpenMobileGroup(activeMobileGroup);
    }
  }, [activeMobileGroup, isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    navigate('/login');
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_rgba(248,250,252,0.96)_0%,_rgba(241,245,249,0.88)_100%)]">
      <nav className="sticky top-0 z-[1200] border-b border-white/60 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-[84px] justify-between gap-4 py-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BrandLogo compact />
              </div>
              <div className="hidden lg:ml-8 lg:flex lg:flex-wrap lg:items-center lg:gap-2">
                {navGroups.map((group) => (
                  <DesktopMenuGroup key={group.label} group={group} pathname={location.pathname} />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isRidesModuleEnabled && (
                <Link
                  to="/rides/book"
                  className="hidden sm:inline-flex items-center rounded-full bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700"
                >
                  Book Ride
                </Link>
              )}
              {showSosAccess && (
                <Link
                  to="/sos"
                  className="hidden sm:inline-flex items-center rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
                >
                  SOS
                </Link>
              )}
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-100 hover:text-slate-900 lg:hidden"
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
              </button>

              <div className="hidden lg:flex lg:items-center lg:gap-2">
                <Link
                  to="/about"
                  className="inline-flex items-center rounded-full px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 hover:text-slate-900 whitespace-nowrap"
                >
                  About Us
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-full px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 hover:text-slate-900 whitespace-nowrap"
                >
                  Contact Us
                </Link>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                className="hidden lg:inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 whitespace-nowrap"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && createPortal(
          <div className="fixed inset-0 z-[1300] bg-slate-950/45 backdrop-blur-sm lg:hidden" onClick={closeMobileMenu}>
            <div
              className="ml-auto flex h-dvh w-full max-w-[420px] flex-col border-l border-slate-200 bg-slate-50 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex min-h-[86px] items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-3">
                <BrandLogo compact />
                <button
                  type="button"
                  onClick={closeMobileMenu}
                  className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100"
                  aria-label="Close navigation menu"
                >
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="border-b border-slate-200 bg-white px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Account menu</p>
                <p className="mt-1 text-lg font-semibold text-slate-950">{currentUser?.role ? `${currentUser.role.toLowerCase()} workspace` : 'LocalAlly workspace'}</p>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4">
                {isRidesModuleEnabled && (
                  <Link
                    to="/rides/book"
                    onClick={closeMobileMenu}
                    className="block rounded-xl bg-teal-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700"
                  >
                    Book Ride
                  </Link>
                )}
                {showSosAccess && (
                  <Link
                    to="/sos"
                    onClick={closeMobileMenu}
                    className="block rounded-xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700"
                  >
                    Emergency SOS
                  </Link>
                )}
                {navGroups.map((group) => (
                  <MobileMenuGroup
                    key={group.label}
                    group={group}
                    pathname={location.pathname}
                    isOpen={openMobileGroup === group.label}
                    onToggle={() => setOpenMobileGroup((current) => (current === group.label ? '' : group.label))}
                    onNavigate={closeMobileMenu}
                  />
                ))}
              </div>

              <div className="border-t border-slate-200 bg-white p-4">
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    to="/about"
                    onClick={closeMobileMenu}
                    className="inline-flex min-h-[46px] items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                  >
                    About Us
                  </Link>
                  <Link
                    to="/contact"
                    onClick={closeMobileMenu}
                    className="inline-flex min-h-[46px] items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                  >
                    Contact Us
                  </Link>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="mt-3 inline-flex min-h-[46px] w-full items-center justify-center rounded-lg bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      </nav>

      <div className="py-10">
        <main id="main-content" tabIndex={-1} className="focus:outline-none">
          <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="px-4 py-8 sm:px-0">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {showSosAccess && (
        <Link
          to="/sos"
          className="fixed bottom-6 right-6 z-[900] inline-flex items-center rounded-full bg-rose-600 px-5 py-3 text-sm font-semibold text-white shadow-[0_20px_45px_-18px_rgba(190,24,93,0.75)] transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2"
        >
          Emergency SOS
        </Link>
      )}

      <SharedFooter compact />
    </div>
  );
};

export default Layout;
